import { useMutation, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { usePostHog } from 'posthog-js/react';
import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';

import { type UserData } from '@/hooks/useUserData.js';
import { TourProvider } from '@/onboarding_tour/TourProvider.js';
import { TRACK_USER_SEEN } from '@/services/user.service.js';

import { GetCurrentUserDocument } from '../__generated__/graphql.js';
import { GrovePath } from '../config/paths.js';
import { ErrorPage, ErrorType } from '../pages/ErrorPage.js';
import Home from '../pages/home/Home.js';
import { AdminProvider } from '../providers/AdminProvider.js';
import { CurrentUserProvider } from '../providers/CurrentUserProvider.js';
import { AuthGuard } from './AuthGuard.js';
import Loading from './Loading.js';
import MovedToWebApp from './MovedToWebApp.js';
import Sidebar from './Sidebar.js';
import TopNavBar from './TopNavBar.js';

const LazyIntegrations = React.lazy(() => import('@/pages/asset/AssetIntegrations.js'));
const LazyAssetPreferences = React.lazy(() => import('@/pages/asset/preferences/AssetPreferences.js'));
const LazyClientHealthScore = React.lazy(() => import('@/pages/ClientHealthTable.js'));
const LazyLogin = React.lazy(() => import('@/pages/Login.js'));
const LazyAssetHome = React.lazy(() => import('../pages/asset/asset-home.js'));
const LazyAssetDownloads = React.lazy(() => import('../pages/asset/AssetDownloads.js'));
// const LazyAssetGallery = React.lazy(() => import('../pages/asset/AssetGallery.js'));
const LazyAssetHoneyLabel = React.lazy(() => import('../pages/asset/AssetHoneyLabel.js'));
const LazyAssetWorkshops = React.lazy(() => import('../pages/asset/AssetWorkshops.js'));
const LazyReportingHome = React.lazy(() => import('../pages/reporting/ReportingHome.js'));
const LazyUnsubscribe = React.lazy(() => import('../pages/Unsubscribe.js'));
const LazyPortfolioDashboard = React.lazy(() => import('@/pages/portfolio_dashboard/PortfolioDashboard.js'));
const LazyFreeTool = React.lazy(() => import('../pages/free-tool/FreeToolContainer.js'));
const LazyFreeToolMultiAsset = React.lazy(() => import('../pages/free-tool/FreeToolMultiAsset.js'));
const LazySignup = React.lazy(() => import('@/pages/Signup.js'));

const LazySamlComplete = React.lazy(() => import('@/pages/SamlComplete.js'));
const LazyBlogAutomation = React.lazy(() => import('../pages/BlogAutomation.js'));
const LazyBioacoustics = React.lazy(() => import('../pages/bioacoustics/BioacousticsPage.js'));
const LazyEDNA = React.lazy(() => import('../pages/edna/EDNAPage.js'));

export const App: React.FC = () => {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const [trackUserSeen] = useMutation(TRACK_USER_SEEN);
  const posthog = usePostHog();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // The VTS SAML handoff page must always be allowed to run, even when a stale
  // session already exists. It exchanges a one-time code and re-stamps the Auth0
  // cache; if the getCurrentUser gate below ran here, an "authenticated but
  // unusable" session would error, render LoadUserError, and preempt this route
  // (the early return is above <Routes>), so the fresh code would never be
  // consumed and the user could never recover. See SamlComplete.tsx.
  const isSamlHandoff = pathname === (GrovePath.SamlComplete as string);

  const {
    data: user,
    loading: isLoadingUser,
    error: userLoadError,
  } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated || isSamlHandoff,
  });

  const currentUserValue = {
    user: user?.getCurrentUser,
    loading: isLoading || isLoadingUser,
    error: userLoadError,
  };

  // Read attribution params from URL and register them as PostHog super properties.
  // The parent page's posthog_distinct_id is no longer handled here — it's read
  // synchronously and passed via bootstrap.distinctID in CombinedProviders, before
  // PostHogProvider mounts, so identity is correct from the very first event.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const attributionKeys = ['lp_variant'] as const;

    const superProperties: Record<string, string> = {};
    for (const key of attributionKeys) {
      const value = params.get(key);
      if (value) {
        superProperties[key] = value;
      }
    }

    if (Object.keys(superProperties).length > 0) {
      posthog.register(superProperties);
    }
  }, [posthog]);

  useEffect(() => {
    if(userLoadError){
      console.log(userLoadError);
    }
  }, [userLoadError]);

  useEffect(() => {
    const sanitizedParams = new URLSearchParams(searchParams.toString());
    const shouldLogin = sanitizedParams.get('login') === 'true';
    const email = searchParams.get('email');
    if (shouldLogin && !isLoading) {
      const sanitizedQuery = sanitizedParams.toString();
      const sanitizedUrl =
        `${window.location.origin}${pathname}` + (sanitizedQuery ? `?${sanitizedQuery}` : '') + window.location.hash;

      if (isAuthenticated) {
        if (sanitizedParams.has('login')) {
          sanitizedParams.delete('login');
          setSearchParams(sanitizedParams, { replace: true });
        }
        if (sanitizedParams.has('email')) {
          sanitizedParams.delete('email');
          setSearchParams(sanitizedParams, { replace: true });
        }
      } else {
        void loginWithRedirect({
          authorizationParams: { login_hint: email?.length ? email : undefined },
          appState: { returnTo: sanitizedUrl },
        });
      }
    }
  }, [isLoading, isAuthenticated, searchParams, setSearchParams, loginWithRedirect, pathname]);

  useEffect(() => {
    if (user?.getCurrentUser) {
      posthog.identify(user.getCurrentUser.id, {
        email: user.getCurrentUser.email,
        authenticated: true,
      });
      posthog.alias(user.getCurrentUser.email, user.getCurrentUser.id);
    } else if (localStorage.getItem('userData') && !isLoading && !isAuthenticated) {
      const { email } = JSON.parse(localStorage.getItem('userData') ?? '{}') as UserData;
      if (typeof email === 'string' && email.length > 0) {
        posthog.setPersonProperties({
          email,
          authenticated: false,
        });
      }
    }
  }, [posthog, user, isAuthenticated, isLoading]);

  useEffect(() => {
    if (user?.getCurrentUser) {
      void (async () => {
        await trackUserSeen();
      })();
    }
  }, [user, isLoading, isAuthenticated]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const AdminContext = (
    <AdminProvider>
      <Outlet />
    </AdminProvider>
  );

  if (isLoading || isLoadingUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loading />
      </div>
    );
  }

  const Nav = isAuthenticated ? (
    <CurrentUserProvider value={currentUserValue}>
      <AdminProvider>
        <TourProvider>
          <Sidebar>
            <Outlet />
          </Sidebar>
        </TourProvider>
      </AdminProvider>
    </CurrentUserProvider>
  ) : (
    <TopNavBar>
      <Outlet />
    </TopNavBar>
  );

  if (userLoadError && isAuthenticated && !isSamlHandoff) {
    return (
      <CurrentUserProvider value={currentUserValue}>
        <AdminProvider>
          <Sidebar>
            <ErrorPage errorType={ErrorType.LoadUserError} />
          </Sidebar>
        </AdminProvider>
      </CurrentUserProvider>
    );
  }

  return (
    <CurrentUserProvider value={currentUserValue}>
      <ErrorBoundary fallback={<ErrorPage errorType={ErrorType.GenericError} />}>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen">
              <Loading />
            </div>
          }
        >
          <Routes>
            <Route path={GrovePath.Bioacoustics} element={<LazyBioacoustics />} />
            <Route path={GrovePath.EDNA} element={<LazyEDNA />} />
            <Route path={GrovePath.AuraFreeTool} element={<LazyFreeTool />} />
            <Route path={GrovePath.FreeToolMultiAsset} element={<LazyFreeToolMultiAsset />} />
            <Route path={GrovePath.ClientHealthScore} element={<LazyClientHealthScore />} />
            {/* MOVED TO THE WEB APP (2026-10-07): kiosk mode and every
                control-hub page. The redirect Worker handles document loads;
                these catch links clicked inside this app, signed in or not. */}
            <Route
              path={GrovePath.KioskMode}
              element={<MovedToWebApp to={({ zuperJobUid }) => `/kiosk/${zuperJobUid ?? ''}`} />}
            />
            <Route path={GrovePath.AdminControlHub} element={<MovedToWebApp to={() => '/admin-general'} />} />
            <Route path={GrovePath.AdminUserManagement} element={<MovedToWebApp to={() => '/admin-general'} />} />
            <Route
              path={GrovePath.BeekeeperAdmin}
              element={<MovedToWebApp to={() => '/admin-general?role=BEEKEEPER'} />}
            />
            <Route path={GrovePath.PagesAdmin} element={<MovedToWebApp to={() => '/admin-general/sites'} />} />
            <Route
              path={GrovePath.PagesAdminDetail}
              element={<MovedToWebApp to={({ slug }) => `/admin-general/sites/${slug ?? ''}`} />}
            />
            <Route path={GrovePath.PageGroupsAdmin} element={<MovedToWebApp to={() => '/admin-general/site-groups'} />} />
            <Route path={GrovePath.RegionsAdmin} element={<MovedToWebApp to={() => '/admin-general/regions'} />} />
            <Route
              path={GrovePath.BouncingUsersAdmin}
              element={<MovedToWebApp to={() => '/admin-general/sites?contact=BOUNCING'} />}
            />
            <Route path={GrovePath.SamlComplete} element={<LazySamlComplete />} />
            <Route element={AdminContext}>
              <Route index element={<Home />} />
              <Route path={GrovePath.Login} element={<LazyLogin />} />
              <Route path={GrovePath.Signup} element={<LazySignup />} />
              <Route element={Nav}>
                <Route path={GrovePath.PortfolioDashboard} element={<LazyPortfolioDashboard />} />
                <Route path={GrovePath.Asset} element={<LazyAssetHome />} />
                <Route path={GrovePath.Unsubscribe} element={<LazyUnsubscribe />} />
                <Route element={<AuthGuard />}>
                  <Route path={GrovePath.AssetWorkshops} element={<LazyAssetWorkshops />} />
                  <Route path={GrovePath.AssetHoneyLabel} element={<LazyAssetHoneyLabel />} />
                  <Route path={GrovePath.AssetIntegrations} element={<LazyIntegrations />} />
                  {/* <Route path={GrovePath.AssetGallery} element={<LazyAssetGallery />} /> */}
                  <Route path={GrovePath.AssetDownloads} element={<LazyAssetDownloads />} />
                  <Route path={GrovePath.AssetPreferences} element={<LazyAssetPreferences />} />
                </Route>
                <Route path="*" element={<ErrorPage errorType={ErrorType.PageNotFound} />} />
              </Route>
              <Route element={<AuthGuard />}>
                <Route path={GrovePath.Reporting} element={<LazyReportingHome />} />
                <Route path={GrovePath.BlogAutomation} element={<LazyBlogAutomation />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </CurrentUserProvider>
  );
};
